import React, { FC } from 'react';
import { Button, Theme, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useIntl } from 'react-intl';
import moment from 'moment-timezone';
import classNames from 'classnames';
import unMessages from 'common/messages/userNotifications';
import { colors } from 'common/ui/theme/default';
import { IUserNotification } from 'interfaces/userNotification.interfaces';
import useUserNotificationsContext from 'common/hooks/context/useUserNotificationsContext';

const useStyles = makeStyles((theme: Theme) => ({
  menuItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '8px !important',
    borderRadius: '4px',
    gap: '12px',
    cursor: 'default',
    '&:hover': {
      color: 'initial',
      backgroundColor: colors.background.page,
    },
  },
  menuItemImg: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '36px',
    height: '36px',
    borderRadius: '4px',
    '& img': {
      objectFit: 'cover',
      width: '100%',
      height: '100%',
    },
  },
  menuItemText: {
    maxWidth: '280px',
    flex: 1,
  },
  menuItemTitle: {
    fontSize: '14px',
    fontWeight: 600,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  menuItemDescription: {
    fontSize: '12px',
    fontWeight: 400,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    color: colors.text.secondary,
  },
  menuItemInfo: {
    width: 'max-content',
    minWidth: '126px',
    textAlign: 'right',
  },
  menuItemDate: {
    fontSize: '12px',
    color: colors.text.secondary,
  },
  menuItemBtnMarkLoading: {
    pointerEvents: 'none',
  },
  menuItemBtnMark: {
    width: '12px',
    height: '12px',
    minWidth: 'auto',
    borderRadius: '50%',
    padding: '0',
    boxShadow: 'unset',
  },
  menuItemBtnRead: {
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      boxShadow: `0 0 4px ${theme.palette.primary.main}`,
    },
  },
  menuItemBtnUnread: {
    width: '11px',
    height: '11px',
    backgroundColor: 'white',
    border: `1px solid ${theme.palette.primary.main}`,
    '&:hover': {
      boxShadow: `0 0 4px ${theme.palette.primary.main}`,
    },
  },
}));

interface IUserNotificationsMenuItem {
  userNotification: IUserNotification;
}

const UserNotificationsMenuItem: FC<IUserNotificationsMenuItem> = ({ userNotification }) => {
  const { readUserNotification, readUserNotificationLoading } = useUserNotificationsContext();
  const classes = useStyles();
  const intl = useIntl();
  let btnMarkTooltipTitle: string;
  let btnMarkClassName: string;

  if (userNotification.read) {
    btnMarkTooltipTitle = intl.formatMessage(unMessages.userNotificationsMarkAsUnread);
    btnMarkClassName = classes.menuItemBtnUnread;
  } else {
    btnMarkTooltipTitle = intl.formatMessage(unMessages.userNotificationsMarkAsRead);
    btnMarkClassName = classes.menuItemBtnRead;
  }

  const onMarkUserNotification = (): void => {
    void readUserNotification(userNotification.id, !userNotification.read);
  };

  return (
    <div key={userNotification.id} className={classes.menuItem}>
      <div className={classes.menuItemImg}>
        <img src={userNotification.image.url} alt="icon" />
      </div>

      <div className={classes.menuItemText}>
        <div className={classes.menuItemTitle}>{userNotification.title}</div>
        <div className={classes.menuItemDescription}>{userNotification.description}</div>
      </div>

      <div className={classes.menuItemInfo}>
        <div className={classes.menuItemDate}>
          {moment(userNotification.createdDate).format('LLL')}
        </div>

        <Tooltip title={btnMarkTooltipTitle} placement="bottom-end">
          <Button
            onClick={onMarkUserNotification}
            className={classNames(classes.menuItemBtnMark, btnMarkClassName, {
              [classes.menuItemBtnMarkLoading]: readUserNotificationLoading,
            })}
          />
        </Tooltip>
      </div>
    </div>
  );
};

export default UserNotificationsMenuItem;
